import { z } from 'zod';
import { ColorSchema, LOOK, NameSchema, about, numberFormat, sourceOf } from '../schema_fields.ts';
import { ThresholdSchema } from './gauge_look.ts';

// The graph tile: readings over time, as lines or bars, with axes, a legend and the values at a tap
// (docs/specs/graph-tile.md).

export const GRAPH_FILLS = ['none', 'solid', 'fade'] as const;
export type GraphFill = (typeof GRAPH_FILLS)[number];

export const GraphSchema = z.object({
    type: z.enum(['line', 'bar']).default('line').meta({ ...about('Kind', 'lines or bars'), 'x-labels': { line: 'Lines', bar: 'Bars' } }),
    /** Device pixels, as in the legacy view. */
    lineWidth: z.number().positive().default(1).meta({ ...about('Line width', 'in pixels'), ...LOOK }),
    series: z
        .array(
            z.object({
                source: sourceOf(NameSchema, { gives: ['history', 'number'], label: 'Readings' }),
                color: ColorSchema.default('#ffffff').meta(about('Colour', undefined, 'color')),
                cumulative: z.boolean().default(false).meta(about('Adds up', 'each value added to the ones before')),
                /** In the legend and the values at a tap (GR-6, GR-7). */
                label: z.string().optional().meta(about('Label', 'in the legend and the values at a tap')),
                /** Under a line, in its colour (GR-3). */
                fill: z.enum(GRAPH_FILLS).default('none').meta({
                    ...about('Fill', 'the area under a line, in its colour'),
                    'x-labels': { none: 'None', solid: 'Plain', fade: 'Fading out to the bottom' },
                }),
                /** A bar's colour from its value (the owner, 2026-10-29: cheap prices green, dear ones red). */
                // Shown for lines too: a hidden field loses its value in the editor (`setField`), and switching
                // the kind to lines and back would drop the thresholds (the owner chose to show it, 2026-10-29).
                thresholds: z.array(ThresholdSchema).default([]).meta(about('Bar colours', 'bars only: a colour from a value on, such as orange from 0.30 and red from 0.40; the series’ colour below the first')),
            }).meta({ title: 'Series' }),
        )
        .min(1)
        .meta(about('Series', 'the readings shown, each its own line or bars')),
    /** After the values on the value axis and at a tap (GR-1, GR-7). */
    unit: z.string().default('').meta(about('Unit', 'shown after the values, such as W')),
    /** The bars' values and the values at a tap; the axis rounds its own. */
    numberFormat: numberFormat('0.#'),
    /** The value axis from the readings unless fixed (GR-4). */
    min: z.number().optional().meta(about('Lowest value', 'at the bottom; from the readings when left out')),
    max: z.number().optional().meta(about('Highest value', 'at the top; from the readings when left out')),
    /** On for every graph unless turned off (GR-1, QGR-3). */
    timeAxis: z.boolean().default(true).meta({ ...about('Time axis', 'the times along the bottom'), ...LOOK }),
    valueAxis: z.boolean().default(true).meta({ ...about('Value axis', 'the values at the left'), ...LOOK }),
    grid: z.boolean().default(true).meta({ ...about('Grid lines', 'faint lines at the axes’ values'), ...LOOK }),
    legend: z.boolean().default(true).meta({ ...about('Legend', 'the series’ labels, with more than one series'), ...LOOK }),
    barValues: z.boolean().default(false).meta({ ...about('Bar values', 'each bar’s value above it, where there is room'), ...LOOK }),
});

export const GraphOptionsSchema = z.object({ graph: GraphSchema.meta(about('Graph')) });
